import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useDropzone, FileRejection } from "react-dropzone";
import { IComponente } from "../types/componente.types";
import { uploadImagem, uploadIcone, uploadFichaTecnica, definirNomeMidiaComponente } from "../services/componenteService";
import { useNavigation } from "contexts/notificationsContext";
import { CampoNomeArquivo } from "../../components/CampoNomeArquivo";
import { useMidiaCompartilhada } from "../../hooks/useMidiaCompartilhada";
import { nomeArquivoPadrao } from "../../hooks/useNomeArquivoPadrao";

// FASE 05 — 2ª validação/PO 2026-07-24 (D2): Gerenciamento de Mídias do Componente com
// três abas (Ícone/Figura/Ficha Técnica), espelhando o modal do Perfil. Cada aba informa
// a extensão aceita e para que serve o arquivo no CEM.

interface ModalGerenciamentoMidiasProps {
  open: boolean;
  onClose: () => void;
  componente: Partial<IComponente>;
  onSave: (midias: {
    imagemUrl?: string;
    imagemNomeArquivo?: string;
    iconeUrl?: string;
    iconeNomeArquivo?: string;
    fichaTecnicaUrl?: string;
    fichaTecnicaNomeArquivo?: string;
  }, idAlvo: number) => void;
  onRefreshComponente: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`midia-componente-tabpanel-${index}`}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface UploadAreaProps {
  accept: string;
  ajuda: string;
  arquivoAtual?: string;
  nomeArquivo?: string;
  onFileSelect: (file: File) => void;
  onFileRejected?: (motivo: string) => void;
  disabled?: boolean;
}

const MIME_POR_EXTENSAO: Record<string, string[]> = {
  ".bmp": ["image/bmp", "image/x-ms-bmp"],
  ".png": ["image/png"],
  ".jpg": ["image/jpeg"],
  ".jpeg": ["image/jpeg"],
  ".pdf": ["application/pdf"],
};

// FASE 05 — 4ª validação/D8 2026-07-30: aceitar por MIME **e** extensão. O mapa anterior
// (MIME com lista de extensões vazia) fazia o dropzone rejeitar em silêncio arquivos cujo
// browser reporta MIME fora do esperado (caso típico: .bmp) — sem preview, sem erro e sem
// nenhum request (logs IIS de 29/07: zero uploads de Componente).
const montarAccept = (accept: string): Record<string, string[]> => {
  const resultado: Record<string, string[]> = {};
  accept.split(",").forEach((extBruta) => {
    const ext = extBruta.trim().toLowerCase();
    (MIME_POR_EXTENSAO[ext] ?? []).forEach((mime) => {
      if (!resultado[mime]) resultado[mime] = [];
      if (!resultado[mime].includes(ext)) resultado[mime].push(ext);
    });
  });
  return resultado;
};

const UploadArea: React.FC<UploadAreaProps> = ({
  accept,
  ajuda,
  arquivoAtual,
  nomeArquivo,
  onFileSelect,
  onFileRejected,
  disabled,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
        return;
      }
      if (fileRejections.length > 0) {
        onFileRejected?.(
          `Arquivo não aceito. Extensões permitidas: ${accept.toUpperCase()}`
        );
      }
    },
    [onFileSelect, onFileRejected, accept]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: montarAccept(accept),
    multiple: false,
    disabled,
  });

  return (
    <Paper
      {...getRootProps()}
      sx={{
        p: 4,
        textAlign: "center",
        border: isDragActive ? "2px dashed #3C90AD" : "2px dashed #ccc",
        backgroundColor: isDragActive ? "#f0f8ff" : "#fafafa",
        cursor: disabled ? "not-allowed" : "pointer",
        minHeight: 300,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        "&:hover": {
          borderColor: disabled ? "#ccc" : "#3C90AD",
          backgroundColor: disabled ? "#fafafa" : "#f8f8f8",
        },
      }}
    >
      <input {...getInputProps()} />
      {arquivoAtual ? (
        <Box>
          {arquivoAtual.toLowerCase().endsWith(".pdf") ? (
            <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
              📄 PDF carregado
            </Typography>
          ) : (
            <img
              src={arquivoAtual}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain", marginBottom: 16 }}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {nomeArquivo || "Arquivo carregado"}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Arraste outro arquivo ou clique para substituir
          </Typography>
        </Box>
      ) : (
        <Box>
          <CloudUploadIcon sx={{ fontSize: 48, color: "#999", mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Arraste e solte o arquivo aqui dentro ou clique para selecionar
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {ajuda}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export const ModalGerenciamentoMidias: React.FC<ModalGerenciamentoMidiasProps> = ({
  open,
  onClose,
  componente,
  onSave,
  onRefreshComponente,
}) => {
  const { openNotification } = useNavigation();
  const [tabIndex, setTabIndex] = useState(0);
  const [uploading, setUploading] = useState(false);

  const [icone, setIcone] = useState<{ url?: string; nome?: string; file?: File }>({
    url: componente.iconeUrl,
    nome: componente.iconeNomeArquivo,
  });
  const [figura, setFigura] = useState<{ url?: string; nome?: string; file?: File }>({
    url: componente.imagemUrl,
    nome: componente.imagemNomeArquivo,
  });
  const [fichaTecnica, setFichaTecnica] = useState<{ url?: string; nome?: string; file?: File }>({
    url: componente.fichaTecnicaUrl,
    nome: componente.fichaTecnicaNomeArquivo,
  });

  // Reporte de 31/08 (item 6): nomes com que o modal ABRIU. A comparação de "nome alterado"
  // é contra eles, não contra o cadastro — em produto novo a Etapa C já sugere <código>.PNG,
  // e comparar com o cadastro (vazio) habilitaria o SALVAR sem o usuário ter tocado em nada.
  const nomesIniciais = React.useRef<{ icone?: string; figura?: string; fichaTecnica?: string }>({});

  React.useEffect(() => {
    if (open) {
      // Etapa C (item 12 do docx de 22/08): em cadastro NOVO sem nome informado, sugere
      // <código>.BMP/.PNG/.PDF. Cadastro existente mantém o que está gravado.
      const ehNovo = !componente.dataCriacao;
      const nomeIcone = nomeArquivoPadrao(componente.codigo, ".BMP", ehNovo, componente.iconeNomeArquivo);
      const nomeFigura = nomeArquivoPadrao(componente.codigo, ".PNG", ehNovo, componente.imagemNomeArquivo);
      const nomeFicha = nomeArquivoPadrao(componente.codigo, ".PDF", ehNovo, componente.fichaTecnicaNomeArquivo);

      nomesIniciais.current = { icone: nomeIcone, figura: nomeFigura, fichaTecnica: nomeFicha };
      setIcone({ url: componente.iconeUrl, nome: nomeIcone });
      setFigura({ url: componente.imagemUrl, nome: nomeFigura });
      setFichaTecnica({ url: componente.fichaTecnicaUrl, nome: nomeFicha });
    }
  }, [open, componente]);

  const handleFileSelect = (tipo: "icone" | "figura" | "fichaTecnica", file: File) => {
    const url = URL.createObjectURL(file);
    if (tipo === "icone") setIcone({ url, nome: file.name, file });
    else if (tipo === "figura") setFigura({ url, nome: file.name, file });
    else setFichaTecnica({ url, nome: file.name, file });
  };

  const handleSalvar = async () => {
    if (!componente.idComponente) {
      openNotification({
        message: "Salve o componente antes de gerenciar mídias.",
        type: "warning",
      });
      return;
    }

    // Etapa D (F05A-P3b): arquivo compartilhado altera a mídia de TODOS os produtos que o usam.
    const compartilhados = [
      icone.file ? compartilhandoIcone : 0,
      figura.file ? compartilhandoFigura : 0,
      fichaTecnica.file ? compartilhandoFicha : 0,
    ].reduce((a, b) => a + b, 0);
    if (compartilhados > 0) {
      const segue = window.confirm(
        `Algum dos arquivos é usado por outros produtos ( no total). ` +
        `Substituí-lo altera a mídia de todos eles. Deseja continuar?`
      );
      if (!segue) return;
    }

    setUploading(true);
    try {
      // Plano 21/09/2026 (Frente 3): cada gravação devolve o id do registro alterado — com o
      // componente Disponível, a API grava na cópia de trabalho. As chamadas seguintes já vão
      // para esse id, e a página navega para ele ao final.
      let idAlvo = componente.idComponente;
      const midias: {
        imagemUrl?: string;
        imagemNomeArquivo?: string;
        iconeUrl?: string;
        iconeNomeArquivo?: string;
        fichaTecnicaUrl?: string;
        fichaTecnicaNomeArquivo?: string;
      } = {
        imagemUrl: figura.url,
        imagemNomeArquivo: figura.nome,
        iconeUrl: icone.url,
        iconeNomeArquivo: icone.nome,
        fichaTecnicaUrl: fichaTecnica.url,
        fichaTecnicaNomeArquivo: fichaTecnica.nome,
      };

      if (icone.file) {
        // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 3): upload que falha vira ERRO visível
        // (antes o retorno null era tratado como sucesso — toast de sucesso sem mídia gravada).
        const retorno = await uploadIcone(idAlvo, icone.file, icone.nome);
        if (!retorno) {
          throw new Error("Falha no upload do ícone");
        }
        idAlvo = retorno.id;
        midias.iconeUrl = retorno.url;
        midias.iconeNomeArquivo = icone.nome;
      }
      if (figura.file) {
        const retorno = await uploadImagem(idAlvo, figura.file, figura.nome);
        if (!retorno) {
          throw new Error("Falha no upload da figura");
        }
        idAlvo = retorno.id;
        midias.imagemUrl = retorno.url;
        midias.imagemNomeArquivo = figura.nome;
      }
      if (fichaTecnica.file) {
        const retorno = await uploadFichaTecnica(idAlvo, fichaTecnica.file, fichaTecnica.nome);
        if (!retorno) {
          throw new Error("Falha no upload da ficha técnica");
        }
        idAlvo = retorno.id;
        midias.fichaTecnicaUrl = retorno.url;
        midias.fichaTecnicaNomeArquivo = fichaTecnica.nome;
      }

      // Reporte de 31/08 (item 6): aba em que o usuário só DIGITOU o nome (sem escolher
      // arquivo) grava apenas o nome — é como o produto assume uma mídia que já está no
      // acervo da empresa. Sem isto o nome não tinha caminho de gravação nenhum.
      const semArquivo: Array<["icone" | "figura" | "fichaTecnica", string | undefined]> = [];
      if (!icone.file && icone.nome !== nomesIniciais.current.icone) semArquivo.push(["icone", icone.nome]);
      if (!figura.file && figura.nome !== nomesIniciais.current.figura) semArquivo.push(["figura", figura.nome]);
      if (!fichaTecnica.file && fichaTecnica.nome !== nomesIniciais.current.fichaTecnica)
        semArquivo.push(["fichaTecnica", fichaTecnica.nome]);

      const semArquivoNoAcervo: string[] = [];
      for (const [tipo, nome] of semArquivo) {
        if (!nome?.trim()) continue;
        const resultado = await definirNomeMidiaComponente(idAlvo, tipo, nome);
        if (!resultado) {
          throw new Error("Falha ao gravar o nome do arquivo");
        }
        idAlvo = resultado.id ?? idAlvo;
        if (!resultado.arquivoExiste) semArquivoNoAcervo.push(resultado.nomeArquivo);
        if (tipo === "icone") {
          midias.iconeUrl = resultado.url;
          midias.iconeNomeArquivo = resultado.nomeArquivo;
        } else if (tipo === "figura") {
          midias.imagemUrl = resultado.url;
          midias.imagemNomeArquivo = resultado.nomeArquivo;
        } else {
          midias.fichaTecnicaUrl = resultado.url;
          midias.fichaTecnicaNomeArquivo = resultado.nomeArquivo;
        }
      }

      onSave(midias, idAlvo);
      // Se a gravação foi para a cópia de trabalho, a página navega para ela (que recarrega tudo).
      if (idAlvo === componente.idComponente) onRefreshComponente();
      if (semArquivoNoAcervo.length > 0) {
        openNotification({
          message:
            `Nome gravado, mas não há arquivo com este nome no acervo da empresa ` +
            `(${semArquivoNoAcervo.join(", ")}). A imagem só aparecerá quando o arquivo for enviado.`,
          type: "warning",
        });
      } else {
        openNotification({ message: "Mídias salvas com sucesso", type: "success" });
      }
      onClose();
    } catch (error) {
      console.error("Erro ao fazer upload de mídias:", error);
      openNotification({
        message: "Erro ao salvar as mídias — o arquivo NÃO foi gravado. Verifique a extensão aceita na aba e tente novamente.",
        type: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  // Ajustes 25/08 (item 3): há algo novo para gravar nas três abas?
  const temArquivoNovo = !!(icone.file || figura.file || fichaTecnica.file);
  // Reporte de 31/08 (item 6): informar só o NOME também é alteração gravável.
  const temNomeAlterado =
    icone.nome !== nomesIniciais.current.icone ||
    figura.nome !== nomesIniciais.current.figura ||
    fichaTecnica.nome !== nomesIniciais.current.fichaTecnica;

  // Etapa D: quantos OUTROS produtos usam cada um destes nomes.
  const compartilhandoIcone = useMidiaCompartilhada(open, "componentes", 1, icone.nome, componente.idComponente);
  const compartilhandoFigura = useMidiaCompartilhada(open, "componentes", 2, figura.nome, componente.idComponente);
  const compartilhandoFicha = useMidiaCompartilhada(open, "componentes", 3, fichaTecnica.nome, componente.idComponente);

  const semComponente = !componente.idComponente;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxWidth: 700 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ color: "#3C90AD", fontWeight: 500, fontSize: "1rem" }}>
            Gerenciamento de Mídias
          </Typography>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {semComponente && (
          <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
            Salve o componente antes de gerenciar mídias.
          </Typography>
        )}
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{
            minHeight: "auto",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              backgroundColor: "#7ab8cc",
              color: "#fff",
              marginRight: "2px",
              minHeight: 40,
              "&.Mui-selected": { backgroundColor: "#3C90AD", color: "#fff" },
            },
          }}
        >
          <Tab label="ÍCONE" />
          <Tab label="FIGURA" />
          <Tab label="FICHA TÉCNICA" />
        </Tabs>

        <TabPanel value={tabIndex} index={0}>
          <UploadArea
            accept=".bmp"
            ajuda="Arquivo .BMP — utilizado nos relatórios do CEM Desktop."
            arquivoAtual={icone.url}
            nomeArquivo={icone.nome}
            onFileSelect={(file) => handleFileSelect("icone", file)}
            onFileRejected={(motivo) => openNotification({ message: motivo, type: "warning" })}
            disabled={semComponente}
          />
          <CampoNomeArquivo
            valor={icone.nome || ""}
            onChange={(v) => setIcone((a) => ({ ...a, nome: v }))}
            extensoes=".BMP"
            desabilitado={semComponente}
            produtosCompartilhando={compartilhandoIcone}
          />
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          <UploadArea
            accept=".png,.jpg,.jpeg"
            ajuda="Arquivo .PNG ou .JPG — visualização do produto no CEM Desktop e CEM MOBI."
            arquivoAtual={figura.url}
            nomeArquivo={figura.nome}
            onFileSelect={(file) => handleFileSelect("figura", file)}
            onFileRejected={(motivo) => openNotification({ message: motivo, type: "warning" })}
            disabled={semComponente}
          />
          <CampoNomeArquivo
            valor={figura.nome || ""}
            onChange={(v) => setFigura((a) => ({ ...a, nome: v }))}
            extensoes=".PNG, .JPG, .JPEG"
            desabilitado={semComponente}
            produtosCompartilhando={compartilhandoFigura}
          />
        </TabPanel>

        <TabPanel value={tabIndex} index={2}>
          <UploadArea
            accept=".pdf,.jpg,.jpeg"
            ajuda="Arquivo .PDF ou .JPG — visualização de aplicação no CEM Desktop e CEM MOBI."
            arquivoAtual={fichaTecnica.url}
            nomeArquivo={fichaTecnica.nome}
            onFileSelect={(file) => handleFileSelect("fichaTecnica", file)}
            onFileRejected={(motivo) => openNotification({ message: motivo, type: "warning" })}
            disabled={semComponente}
          />
          <CampoNomeArquivo
            valor={fichaTecnica.nome || ""}
            onChange={(v) => setFichaTecnica((a) => ({ ...a, nome: v }))}
            extensoes=".PDF, .JPG, .JPEG"
            desabilitado={semComponente}
            produtosCompartilhando={compartilhandoFicha}
          />
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}>
          CANCELAR
        </Button>
        <Button
          onClick={handleSalvar}
          variant="contained"
          // Ajustes 25/08 (item 3): SALVAR só habilita depois de escolher algum arquivo —
          // antes ficava ativo na abertura, sem nada para gravar.
          disabled={uploading || semComponente || (!temArquivoNovo && !temNomeAlterado)}
          endIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
        >
          {uploading ? "SALVANDO..." : "SALVAR"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
